// PatrollingApi.js - API utilities for Patrolling operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for Patrolling API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.error || errorData.body?.error || 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_patrolling',
        `Patrolling API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[PatrollingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_patrolling',
      `Patrolling API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[PatrollingApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Patrolling Management.
 */
export const patrollingApi = {
  /**
   * Creates a new patrol schedule.
   * POST /patrol/create
   * @param {Object} patrolData - The patrol schedule data.
   * @param {string} [patrolData.patrol_name] - Name of the patrol (optional, auto-generated if not provided).
   * @param {string} patrolData.start_qr_id - QR ID where patrol starts.
   * @param {string} patrolData.end_qr_id - QR ID where patrol ends.
   * @param {Array<string>} patrolData.qr_sequence - Array of QR IDs in sequence.
   * @param {string} patrolData.patrol_start_timestamp - ISO8601 timestamp for patrol start.
   * @param {string} patrolData.patrol_end_timestamp - ISO8601 timestamp for patrol end.
   * @param {string} patrolData.patrol_type - Type of patrol: "on_foot" or "vehicle".
   * @param {Array<Object>} patrolData.patrol_details - Array of patrol detail objects.
   * @param {string} [patrolData.patrol_details[].vehicle_id] - Vehicle ID (required for vehicle patrols).
   * @param {Array<string>} patrolData.patrol_details[].user_ids - Array of user IDs assigned to patrol.
   * @param {string} [patrolData.patrol_details[].material] - Optional material information.
   * @param {string} [patrolData.patrol_details[].notes] - Optional notes.
   * @returns {Promise<Object>} - Response object containing:
   *   - message: Success message
   *   - patrol_id: Generated patrol ID
   *   - attendance_records_created: Number of attendance records created
   * @throws {Error} - Throws an error if the API request fails.
   * 
   * @example
   * // With patrol_name
   * const patrolData = {
   *   patrol_name: "Night Security Patrol",
   *   start_qr_id: "qr-123",
   *   end_qr_id: "qr-456",
   *   qr_sequence: ["qr-123", "qr-234", "qr-345", "qr-456"],
   *   patrol_start_timestamp: "2024-01-15T18:00:00Z",
   *   patrol_end_timestamp: "2024-01-15T22:00:00Z",
   *   patrol_type: "on_foot",
   *   patrol_details: [
   *     {
   *       user_ids: ["user-1", "user-2"],
   *       material: "Flashlight, Radio",
   *       notes: "Check all entry points"
   *     }
   *   ]
   * };
   * 
   * // Without patrol_name (will be auto-generated)
   * const patrolData2 = {
   *   start_qr_id: "qr-123",
   *   end_qr_id: "qr-456",
   *   qr_sequence: ["qr-123", "qr-234", "qr-345", "qr-456"],
   *   patrol_start_timestamp: "2024-01-15T18:00:00Z",
   *   patrol_end_timestamp: "2024-01-15T22:00:00Z",
   *   patrol_type: "vehicle",
   *   patrol_details: [
   *     {
   *       vehicle_id: "vehicle-1",
   *       user_ids: ["user-1"],
   *       notes: "Regular patrol route"
   *     }
   *   ]
   * };
   * 
   * const result = await patrollingApi.createPatrol(patrolData);
   */
  createPatrol: async (patrolData) => {
    try {
      console.log('Patrolling API: Creating new patrol schedule', patrolData);

      // Validate required fields (patrol_name is optional)
      const requiredFields = [
        'start_qr_id',
        'end_qr_id',
        'patrol_start_timestamp',
        'patrol_end_timestamp',
        'patrol_type',
      ];

      const missingFields = requiredFields.filter(field => !patrolData[field]);
      if (missingFields.length > 0) {
        throw new Error(
          `Missing required fields: ${missingFields.join(', ')}`
        );
      }

      // Validate patrol_type
      if (!['on_foot', 'vehicle'].includes(patrolData.patrol_type)) {
        throw new Error('patrol_type must be either "on_foot" or "vehicle"');
      }

      // Validate patrol_details
      if (
        !Array.isArray(patrolData.patrol_details) ||
        patrolData.patrol_details.length === 0
      ) {
        throw new Error('patrol_details must be a non-empty array');
      }

      // Validate vehicle_id for vehicle patrols
      if (patrolData.patrol_type === 'vehicle') {
        const missingVehicleId = patrolData.patrol_details.some(
          detail => !detail.vehicle_id
        );
        if (missingVehicleId) {
          throw new Error('vehicle_id is required for vehicle patrols');
        }
      }

      // Validate user_ids in patrol_details
      const hasUserIds = patrolData.patrol_details.some(
        detail => Array.isArray(detail.user_ids) && detail.user_ids.length > 0
      );
      if (!hasUserIds) {
        throw new Error(
          'At least one user_id must be provided in patrol_details'
        );
      }

      // Validate QR sequence
      if (!Array.isArray(patrolData.qr_sequence)) {
        patrolData.qr_sequence = [];
      }

      // Validate that start_qr_id and end_qr_id are in qr_sequence
      if (
        patrolData.qr_sequence.length > 0 &&
        !patrolData.qr_sequence.includes(patrolData.start_qr_id)
      ) {
        throw new Error('start_qr_id must be in qr_sequence');
      }

      if (
        patrolData.qr_sequence.length > 0 &&
        !patrolData.qr_sequence.includes(patrolData.end_qr_id)
      ) {
        throw new Error('end_qr_id must be in qr_sequence');
      }

      const response = await makeApiRequest(
        '/patrol/create',
        'POST',
        patrolData
      );
      const responseData = await response.json();

      console.log('Patrolling API: Response status:', response.status);
      console.log('Patrolling API: Response data:', responseData);

      if (response.status === 200) {
        // Validate response structure
        if (responseData && typeof responseData === 'object') {
          const result = {
            message: responseData.message || 'Patrol created successfully',
            patrol_id: responseData.patrol_id,
            attendance_records_created:
              responseData.attendance_records_created || 0,
          };
          
          // Log API event for creating patrol
          loggingService.logInfo(
            'api_success_patrolling',
            `Successfully created patrol ${result.patrol_id}`,
            {
              endpoint: '/patrol/create',
              method: 'POST',
              patrolId: result.patrol_id,
              patrolType: patrolData.patrol_type,
              attendanceRecordsCreated: result.attendance_records_created,
            }
          ).catch(logError => {
            console.warn('[PatrollingApi] Failed to log success event:', logError);
          });
          
          return result;
        } else {
          throw new Error('Invalid response format from Patrolling API');
        }
      } else {
        throw new Error(
          `Failed to create patrol: ${response.status} - ${JSON.stringify(responseData)}`
        );
      }
    } catch (error) {
      console.error('Patrolling API: Error in createPatrol:', error);

      // Handle specific error cases based on status codes
      if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 403) {
        throw new Error(
          'You do not have permission to create patrol schedules.'
        );
      } else if (error.status === 400) {
        // Extract error message from response
        const errorMessage =
          error.responseData?.error ||
          error.responseData?.body?.error ||
          error.message;
        throw new Error(`Validation error: ${errorMessage}`);
      } else {
        throw new Error(`Error creating patrol: ${error.message}`);
      }
    }
  },

  /**
   * Fetches all patrols with optional filters.
   * GET /patrol/getall
   * @param {Object} [options={}] - Optional parameters for filtering and pagination.
   * @param {string} [options.status] - Filter by status: "scheduled", "in_progress", "completed", "missed", or "cancelled".
   * @param {string} [options.date] - Filter by date (ISO8601 date string, e.g., "2024-01-15").
   * @param {string} [options.user_id] - Filter by user_id (queries Patrol-Attendance table).
   * @param {number} [options.limit] - Pagination limit (default: 20, max: 100).
   * @param {string} [options.last_key] - Pagination token (JSON string) for next page.
   * @returns {Promise<Object>} - Response object containing:
   *   - patrols: Array of patrol objects
   *   - count: Number of patrols returned
   *   - has_more: Boolean indicating if there are more results
   *   - last_key: Pagination token for next page (null if no more pages)
   * @throws {Error} - Throws an error if the API request fails.
   * 
   * @example
   * // Get all patrols
   * const allPatrols = await patrollingApi.getAll();
   * 
   * // Get patrols by status
   * const scheduledPatrols = await patrollingApi.getAll({ status: 'scheduled' });
   * 
   * // Get patrols by date
   * const todayPatrols = await patrollingApi.getAll({ date: '2024-01-15' });
   * 
   * // Get patrols for a specific user
   * const userPatrols = await patrollingApi.getAll({ user_id: 'user-123' });
   * 
   * // Get patrols with pagination
   * const firstPage = await patrollingApi.getAll({ limit: 20 });
   * const secondPage = await patrollingApi.getAll({ 
   *   limit: 20, 
   *   last_key: firstPage.last_key 
   * });
   * 
   * // Combined filters
   * const filteredPatrols = await patrollingApi.getAll({
   *   status: 'completed',
   *   date: '2024-01-15',
   *   limit: 50
   * });
   */
  getAll: async (options = {}) => {
    try {
      console.log('Patrolling API: Fetching all patrols', options);

      // Build query parameters
      const queryParams = [];
      
      if (options.status) {
        // Validate status
        const validStatuses = ['scheduled', 'in_progress', 'completed', 'missed', 'cancelled'];
        if (!validStatuses.includes(options.status)) {
          throw new Error(
            `Invalid status. Must be one of: ${validStatuses.join(', ')}`
          );
        }
        queryParams.push(`status=${encodeURIComponent(options.status)}`);
      }
      
      if (options.date) {
        // Validate date format (ISO8601 date string: YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(options.date)) {
          throw new Error('Invalid date format. Must be YYYY-MM-DD (e.g., "2024-01-15")');
        }
        queryParams.push(`date=${encodeURIComponent(options.date)}`);
      }
      
      if (options.user_id) {
        queryParams.push(`user_id=${encodeURIComponent(options.user_id)}`);
      }
      
      if (options.limit !== undefined) {
        const limit = Math.min(Math.max(1, parseInt(options.limit, 10)), 100);
        if (isNaN(limit)) {
          throw new Error('Invalid limit. Must be a number between 1 and 100');
        }
        queryParams.push(`limit=${limit}`);
      }
      
      if (options.last_key) {
        queryParams.push(`last_key=${encodeURIComponent(options.last_key)}`);
      }

      // Build endpoint with query parameters
      let endpoint = '/patrol/getall';
      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`;
      }

      const response = await makeApiRequest(endpoint);
      const responseData = await response.json();

      console.log('Patrolling API: Response status:', response.status);
      console.log('Patrolling API: Response data:', responseData);

      if (response.status === 200) {
        // Validate response structure
        if (responseData && typeof responseData === 'object') {
          const result = {
            patrols: Array.isArray(responseData.patrols) ? responseData.patrols : [],
            count: responseData.count || 0,
            has_more: responseData.has_more || false,
            last_key: responseData.last_key || null,
          };
          
          // Log API event for listing patrols
          loggingService.logInfo(
            'api_success_patrolling',
            `Successfully listed patrols`,
            {
              endpoint: '/patrol/getall',
              method: 'GET',
              count: result.count,
              hasMore: result.has_more,
              ...options,
            }
          ).catch(logError => {
            console.warn('[PatrollingApi] Failed to log success event:', logError);
          });
          
          return result;
        } else {
          throw new Error('Invalid response format from Patrolling API');
        }
      } else {
        throw new Error(
          `Failed to fetch patrols: ${response.status} - ${JSON.stringify(responseData)}`
        );
      }
    } catch (error) {
      console.error('Patrolling API: Error in getAll:', error);

      // Handle specific error cases based on status codes
      if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 403) {
        throw new Error(
          'You do not have permission to access patrol schedules.'
        );
      } else if (error.status === 400) {
        // Extract error message from response
        const errorMessage =
          error.responseData?.error ||
          error.responseData?.body?.error ||
          error.message;
        throw new Error(`Validation error: ${errorMessage}`);
      } else {
        throw new Error(`Error fetching patrols: ${error.message}`);
      }
    }
  },

  /**
   * Fetches all patrols and returns only the items array.
   * This is a convenience method that extracts patrols from the paginated response.
   * GET /patrol/getall
   * @param {Object} [options={}] - Optional parameters for filtering (same as getAll).
   * @returns {Promise<Array>} - Array of patrol objects.
   * @throws {Error} - Throws an error if the API request fails.
   * 
   * @example
   * const allPatrols = await patrollingApi.getAllItems();
   * const scheduledPatrols = await patrollingApi.getAllItems({ status: 'scheduled' });
   */
  getAllItems: async (options = {}) => {
    try {
      const response = await patrollingApi.getAll(options);
      return response.patrols || [];
    } catch (error) {
      console.error('Patrolling API: Error in getAllItems:', error);
      throw error;
    }
  },

  /**
   * Get patrol details by patrol_id.
   * GET /patrol/{patrol_id}
   * @param {string} patrol_id - The unique identifier of the patrol to retrieve.
   * @returns {Promise<Object>} - Response object containing patrol details:
   *   - patrol_id: Unique patrol identifier
   *   - tenant_id: Tenant identifier
   *   - patrol_name: Name of the patrol
   *   - start_qr_id: QR ID where patrol starts
   *   - end_qr_id: QR ID where patrol ends
   *   - qr_sequence: Array of QR IDs in sequence
   *   - patrol_start_timestamp: ISO8601 timestamp for patrol start
   *   - patrol_end_timestamp: ISO8601 timestamp for patrol end
   *   - patrol_type: Type of patrol ("on_foot" or "vehicle")
   *   - status: Patrol status ("scheduled", "in_progress", "completed", "missed", "cancelled")
   *   - patrol_details: Array of patrol detail objects
   *   - activity_done_by: User who created/modified the patrol
   *   - event_timestamp: ISO8601 timestamp of last event
   *   - is_scheduled: Boolean indicating if this is a scheduled patrol
   *   - schedule_template_id: Template ID if created from a schedule (null otherwise)
   * @throws {Error} - Throws an error if the API request fails.
   * 
   * @example
   * // Get patrol by ID
   * const patrol = await patrollingApi.getById('a1b2c3d4-5678-90ab-cdef-1234567890ab');
   * console.log(patrol.patrol_name); // "Night Security Patrol"
   * console.log(patrol.status); // "scheduled"
   * console.log(patrol.patrol_type); // "on_foot"
   * 
   * // Example response:
   * {
   *   "patrol_id": "a1b2c3d4-5678-90ab-cdef-1234567890ab",
   *   "tenant_id": "tenant-123",
   *   "patrol_name": "Night Security Patrol",
   *   "start_qr_id": "qr-start-001",
   *   "end_qr_id": "qr-end-001",
   *   "qr_sequence": ["qr-start-001", "qr-checkpoint-001", "qr-checkpoint-002", "qr-end-001"],
   *   "patrol_start_timestamp": "2024-01-15T22:00:00+05:30",
   *   "patrol_end_timestamp": "2024-01-16T06:00:00+05:30",
   *   "patrol_type": "on_foot",
   *   "status": "scheduled",
   *   "patrol_details": [
   *     {
   *       "user_ids": ["user-123", "user-456"],
   *       "vehicle_id": null,
   *       "material": "Flashlight, Radio",
   *       "notes": "Check all entry points"
   *     }
   *   ],
   *   "activity_done_by": "tenant-123/user-789",
   *   "event_timestamp": "2024-01-15T10:30:00+05:30",
   *   "is_scheduled": false,
   *   "schedule_template_id": null
   * }
   */
  getById: async (patrol_id) => {
    try {
      console.log('Patrolling API: Fetching patrol by ID:', patrol_id);

      // Validate patrol_id
      if (!patrol_id || typeof patrol_id !== 'string') {
        throw new Error('Invalid patrol_id. Must be a non-empty string.');
      }

      const endpoint = `/patrol/${encodeURIComponent(patrol_id)}`;
      const response = await makeApiRequest(endpoint);
      const responseData = await response.json();

      console.log('Patrolling API: Response status:', response.status);
      console.log('Patrolling API: Response data:', responseData);

      if (response.status === 200) {
        // Validate response structure
        if (responseData && typeof responseData === 'object' && responseData.patrol_id) {
          // Log API event for getting patrol
          loggingService.logInfo(
            'api_success_patrolling',
            `Successfully fetched patrol ${patrol_id}`,
            {
              endpoint: `/patrol/${patrol_id}`,
              method: 'GET',
              patrolId: patrol_id,
            }
          ).catch(logError => {
            console.warn('[PatrollingApi] Failed to log success event:', logError);
          });
          
          return responseData;
        } else {
          throw new Error('Invalid response format from Patrolling API');
        }
      } else {
        throw new Error(
          `Failed to fetch patrol: ${response.status} - ${JSON.stringify(responseData)}`
        );
      }
    } catch (error) {
      console.error('Patrolling API: Error in getById:', error);

      // Handle specific error cases based on status codes
      if (error.status === 404) {
        throw new Error(`Patrol not found: ${patrol_id}`);
      } else if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 403) {
        throw new Error(
          'You do not have permission to access this patrol.'
        );
      } else if (error.status === 400) {
        // Extract error message from response
        const errorMessage =
          error.responseData?.error ||
          error.responseData?.body?.error ||
          error.message;
        throw new Error(`Validation error: ${errorMessage}`);
      } else {
        throw new Error(`Error fetching patrol: ${error.message}`);
      }
    }
  },

  /**
   * Update an existing patrol schedule (only if status is "scheduled").
   * PUT /patrol/{patrol_id}
   * 
   * This operation will update the patrol and recreate attendance records if patrol_details are changed.
   * 
   * @param {string} patrol_id - The unique identifier of the patrol to update.
   * @param {Object} updateData - The patrol data to update (all fields optional).
   * @param {string} [updateData.patrol_name] - Name of the patrol.
   * @param {string} [updateData.start_qr_id] - QR ID where patrol starts.
   * @param {string} [updateData.end_qr_id] - QR ID where patrol ends.
   * @param {Array<string>} [updateData.qr_sequence] - Array of QR IDs in sequence.
   * @param {string} [updateData.patrol_start_timestamp] - ISO8601 timestamp for patrol start.
   * @param {string} [updateData.patrol_end_timestamp] - ISO8601 timestamp for patrol end.
   * @param {string} [updateData.patrol_type] - Type of patrol: "on_foot" or "vehicle".
   * @param {Array<Object>} [updateData.patrol_details] - Array of patrol detail objects.
   * @param {string} [updateData.patrol_details[].vehicle_id] - Vehicle ID (required for vehicle patrols).
   * @param {Array<string>} [updateData.patrol_details[].user_ids] - Array of user IDs assigned to patrol.
   * @param {string} [updateData.patrol_details[].material] - Optional material information.
   * @param {string} [updateData.patrol_details[].notes] - Optional notes.
   * @returns {Promise<Object>} - Response object containing:
   *   - message: Success message
   *   - patrol_id: The updated patrol ID
   *   - patrol: The updated patrol object
   * @throws {Error} - Throws an error if the API request fails or if patrol status is not "scheduled".
   * 
   * @example
   * // Update patrol name and timestamps
   * const updateData = {
   *   patrol_name: "Updated Night Patrol",
   *   patrol_start_timestamp: "2024-01-16T18:00:00Z",
   *   patrol_end_timestamp: "2024-01-16T22:00:00Z"
   * };
   * const result = await patrollingApi.updatePatrol('patrol-id-123', updateData);
   * 
   * @example
   * // Update patrol type and details
   * const updateData = {
   *   patrol_type: "vehicle",
   *   patrol_details: [
   *     {
   *       vehicle_id: "vehicle-1",
   *       user_ids: ["user-1", "user-2"],
   *       notes: "Updated patrol route"
   *     }
   *   ]
   * };
   * const result = await patrollingApi.updatePatrol('patrol-id-123', updateData);
   * 
   * @example
   * // Update QR sequence
   * const updateData = {
   *   start_qr_id: "qr-123",
   *   end_qr_id: "qr-789",
   *   qr_sequence: ["qr-123", "qr-456", "qr-789"]
   * };
   * const result = await patrollingApi.updatePatrol('patrol-id-123', updateData);
   * 
   * @example
   * // Error case - trying to update a non-scheduled patrol
   * try {
   *   await patrollingApi.updatePatrol('patrol-in-progress-id', { patrol_name: "New Name" });
   * } catch (error) {
   *   console.error(error.message); 
   *   // "Cannot update patrol with status "in_progress". Only "scheduled" patrols can be updated."
   * }
   */
  updatePatrol: async (patrol_id, updateData) => {
    try {
      console.log('Patrolling API: Updating patrol:', patrol_id, updateData);

      // Validate patrol_id
      if (!patrol_id || typeof patrol_id !== 'string') {
        throw new Error('Invalid patrol_id. Must be a non-empty string.');
      }

      // Validate updateData
      if (!updateData || typeof updateData !== 'object' || Object.keys(updateData).length === 0) {
        throw new Error('Update data must be a non-empty object with at least one field to update.');
      }

      // Validate patrol_type if provided
      if (updateData.patrol_type && !['on_foot', 'vehicle'].includes(updateData.patrol_type)) {
        throw new Error('patrol_type must be either "on_foot" or "vehicle"');
      }

      // Validate patrol_details if provided
      if (updateData.patrol_details !== undefined) {
        if (!Array.isArray(updateData.patrol_details) || updateData.patrol_details.length === 0) {
          throw new Error('patrol_details must be a non-empty array');
        }

        // Validate vehicle_id for vehicle patrols
        if (updateData.patrol_type === 'vehicle') {
          const missingVehicleId = updateData.patrol_details.some(
            detail => !detail.vehicle_id
          );
          if (missingVehicleId) {
            throw new Error('vehicle_id is required for vehicle patrols in patrol_details');
          }
        }
      }

      // Validate qr_sequence if provided
      if (updateData.qr_sequence !== undefined) {
        if (!Array.isArray(updateData.qr_sequence)) {
          throw new Error('qr_sequence must be an array');
        }

        // If both start_qr_id and qr_sequence are provided, validate start_qr_id is in sequence
        if (updateData.start_qr_id && !updateData.qr_sequence.includes(updateData.start_qr_id)) {
          throw new Error('start_qr_id must be in qr_sequence');
        }

        // If both end_qr_id and qr_sequence are provided, validate end_qr_id is in sequence
        if (updateData.end_qr_id && !updateData.qr_sequence.includes(updateData.end_qr_id)) {
          throw new Error('end_qr_id must be in qr_sequence');
        }
      }

      const endpoint = `/patrol/${encodeURIComponent(patrol_id)}`;
      const response = await makeApiRequest(endpoint, 'PUT', updateData);
      const responseData = await response.json();

      console.log('Patrolling API: Response status:', response.status);
      console.log('Patrolling API: Response data:', responseData);

      if (response.status === 200) {
        // Validate response structure
        if (responseData && typeof responseData === 'object') {
          const result = {
            message: responseData.message || 'Patrol updated successfully',
            patrol_id: responseData.patrol_id,
            patrol: responseData.patrol || null,
          };
          
          // Log API event for updating patrol
          loggingService.logInfo(
            'api_success_patrolling',
            `Successfully updated patrol ${result.patrol_id || patrol_id}`,
            {
              endpoint: `/patrol/${patrol_id}`,
              method: 'PUT',
              patrolId: result.patrol_id || patrol_id,
              updatedFields: Object.keys(updateData || {}),
            }
          ).catch(logError => {
            console.warn('[PatrollingApi] Failed to log success event:', logError);
          });
          
          return result;
        } else {
          throw new Error('Invalid response format from Patrolling API');
        }
      } else {
        throw new Error(
          `Failed to update patrol: ${response.status} - ${JSON.stringify(responseData)}`
        );
      }
    } catch (error) {
      console.error('Patrolling API: Error in updatePatrol:', error);

      // Handle specific error cases based on status codes
      if (error.status === 404) {
        throw new Error(`Patrol not found: ${patrol_id}`);
      } else if (error.status === 400) {
        // Extract error message from response (includes status validation)
        const errorMessage =
          error.responseData?.error ||
          error.responseData?.body?.error ||
          error.message;
        throw new Error(errorMessage);
      } else if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 403) {
        throw new Error(
          'You do not have permission to update this patrol.'
        );
      } else {
        throw new Error(`Error updating patrol: ${error.message}`);
      }
    }
  },

  /**
   * Delete a patrol schedule (only if status is "scheduled" or "cancelled").
   * DELETE /patrol/{patrol_id}
   * 
   * This operation will delete:
   * - The patrol record from Patrol-Schedules
   * - All attendance records from Patrol-Attendance
   * - All QR check-ins from Patrol-QR-Checkins
   * - All location records from Patrol-Locations
   * 
   * @param {string} patrol_id - The unique identifier of the patrol to delete.
   * @returns {Promise<Object>} - Response object containing:
   *   - message: Success message
   *   - patrol_id: The deleted patrol ID
   * @throws {Error} - Throws an error if the API request fails or if patrol status is not "scheduled" or "cancelled".
   * 
   * @example
   * // Delete a scheduled patrol
   * try {
   *   const result = await patrollingApi.deletePatrol('a1b2c3d4-5678-90ab-cdef-1234567890ab');
   *   console.log(result.message); // "Patrol deleted successfully"
   *   console.log(result.patrol_id); // "a1b2c3d4-5678-90ab-cdef-1234567890ab"
   * } catch (error) {
   *   if (error.message.includes('Cannot delete patrol with status')) {
   *     console.error('Patrol cannot be deleted due to its status');
   *   }
   * }
   * 
   * @example
   * // Error case - trying to delete an in-progress or completed patrol
   * try {
   *   await patrollingApi.deletePatrol('patrol-in-progress-id');
   * } catch (error) {
   *   console.error(error.message); 
   *   // "Cannot delete patrol with status "in_progress". Only "scheduled" or "cancelled" patrols can be deleted."
   * }
   */
  deletePatrol: async (patrol_id) => {
    try {
      console.log('Patrolling API: Deleting patrol:', patrol_id);

      // Validate patrol_id
      if (!patrol_id || typeof patrol_id !== 'string') {
        throw new Error('Invalid patrol_id. Must be a non-empty string.');
      }

      const endpoint = `/patrol/${encodeURIComponent(patrol_id)}`;
      const response = await makeApiRequest(endpoint, 'DELETE');
      const responseData = await response.json();

      console.log('Patrolling API: Response status:', response.status);
      console.log('Patrolling API: Response data:', responseData);

      if (response.status === 200) {
        // Validate response structure
        if (responseData && typeof responseData === 'object') {
          const result = {
            message: responseData.message || 'Patrol deleted successfully',
            patrol_id: responseData.patrol_id,
          };
          
          // Log API event for deleting patrol
          loggingService.logInfo(
            'api_success_patrolling',
            `Successfully deleted patrol ${result.patrol_id || patrol_id}`,
            {
              endpoint: `/patrol/${patrol_id}`,
              method: 'DELETE',
              patrolId: result.patrol_id || patrol_id,
            }
          ).catch(logError => {
            console.warn('[PatrollingApi] Failed to log success event:', logError);
          });
          
          return result;
        } else {
          throw new Error('Invalid response format from Patrolling API');
        }
      } else {
        throw new Error(
          `Failed to delete patrol: ${response.status} - ${JSON.stringify(responseData)}`
        );
      }
    } catch (error) {
      console.error('Patrolling API: Error in deletePatrol:', error);

      // Handle specific error cases based on status codes
      if (error.status === 404) {
        throw new Error(`Patrol not found: ${patrol_id}`);
      } else if (error.status === 400) {
        // Extract error message from response (includes status validation)
        const errorMessage =
          error.responseData?.error ||
          error.responseData?.body?.error ||
          error.message;
        throw new Error(errorMessage);
      } else if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 403) {
        throw new Error(
          'You do not have permission to delete this patrol.'
        );
      } else {
        throw new Error(`Error deleting patrol: ${error.message}`);
      }
    }
  },
};
